import React, { useMemo, useState } from 'react';
import axios from 'axios';
import Button from '../../../../components/Button';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Stack,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from '@mui/material';
import { createVacuumCleaner } from '../../../../services/vacuumService';
import { createCarWashing } from '../../../../services/carWashingsService';
import { createLaundryMachine } from '../../../../services/laundryService';

type Environment = 'PRD' | 'DEV';
type DeviceType = 'VACUUM' | 'WASH' | 'LAUNDRY';
type Mode = 'TEST' | 'CREATE';

const TestRegisterDevice: React.FC = () => {
  const urlPrd = 'https://api.redezandona.com.br/api/v1';
  const urlDev = 'https://core-staging.zandona.elemental.kubescript.io/api/v1';

  const vacuumcleanerSufix = '/vacuumcleaner/publish';
  const laundrySufix = '/laundry-machines/publish';
  const washSufix = '/machine/publish';

  // UI state
  const [mode, setMode] = useState<Mode>('TEST');
  const [env, setEnv] = useState<Environment>('PRD');
  const [deviceType, setDeviceType] = useState<DeviceType>('VACUUM');

  const [stationCode, setStationCode] = useState(''); // stationCode (teste) / stationCode (lavacao cadastro) / station (aspirador cadastro) / stationId (lavanderia cadastro)
  const [deviceId, setDeviceId] = useState('');

  // Máscara (usado somente no teste de Aspirador -> campo "mask" do payload)
  const [mask, setMask] = useState('');

  // Campo "Nome" (cadastro de aspirador e lavanderia)
  const [name, setName] = useState('');

  // Dynamic fields - TESTE
  const [timeOn, setTimeOn] = useState<string>(''); // vacuum test
  const [washingType, setWashingType] = useState<'L1' | 'L2' | 'L3' | 'L4'>('L1'); // wash test
  const [laundryType, setLaundryType] = useState<'L' | 'S'>('L'); // laundry test

  const [pulse, setPulse] = useState<string>(''); // laundry test (e tbm pode reaproveitar no create se quiser)
  const [pulseDuration, setPulseDuration] = useState<string>('');
  const [pulseInterval, setPulseInterval] = useState<string>('');

  // Dynamic fields - CADASTRO (lavanderia)
  const [laundryTypesText, setLaundryTypesText] = useState<string>(''); // ex: "L12,S12"

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');

  const baseUrl = useMemo(() => (env === 'PRD' ? urlPrd : urlDev), [env]);

  const url = useMemo(() => {
    if (deviceType === 'VACUUM') return baseUrl + vacuumcleanerSufix;
    if (deviceType === 'WASH') return baseUrl + washSufix;
    return baseUrl + laundrySufix;
  }, [baseUrl, deviceType]);

  const parseLaundryTypes = (raw: string): string[] => {
    // aceita: "L12,S12" ou "L12 S12" ou em linhas
    return raw
      .split(/[,;\n\r\t ]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  // Payload TESTE (axios post)
  const buildTestPayload = () => {
    if (deviceType === 'VACUUM') {
      return {
        unit: 'cpu/relay',
        cmd: 'pulse_on',
        channel: '0',
        mask,
        duration: timeOn,
      };
    }

    if (deviceType === 'WASH') {
      return {
        id: deviceId,
        washingType,
        stationCode,
      };
    }

    return {
      deviceId,
      type: laundryType,
      pulse: Number(pulse),
      pulseDuration: Number(pulseDuration),
      pulseInterval: Number(pulseInterval),
    };
  };

  // Payload CADASTRO (createX)
  const buildCreatePayload = () => {
    if (deviceType === 'VACUUM') {
      return {
        deviceId,
        name,
        station: stationCode,
      };
    }

    if (deviceType === 'WASH') {
      return {
        washingMachine: deviceId,
        stationCode,
      };
    }

    return {
      deviceId,
      name,
      pulse: Number(pulse) || 0,
      pulseDuration: Number(pulseDuration) || 0,
      pulseInterval: Number(pulseInterval) || 0,
      stationCode: stationCode,
      types: parseLaundryTypes(laundryTypesText),
    };
  };

  const validateTest = (): string | null => {
    if (deviceType === 'VACUUM') {
      if (!mask.trim()) return 'Máscara é obrigatória.';
      if (timeOn.trim() === '') return 'Tempo ligado (ms) é obrigatório.';
      if (Number.isNaN(Number(timeOn)) || Number(timeOn) <= 0) return 'Tempo ligado precisa ser um número > 0.';
      return null;
    }

    if (!deviceId.trim()) return 'DeviceId é obrigatório.';
    if (!stationCode.trim()) return 'Posto é obrigatório.';

    if (deviceType === 'LAUNDRY') {
      const nPulse = Number(pulse);
      const nDur = Number(pulseDuration);
      const nInt = Number(pulseInterval);

      if (pulse.trim() === '' || pulseDuration.trim() === '' || pulseInterval.trim() === '') {
        return 'Pulse, pulseDuration e pulseInterval são obrigatórios.';
      }
      if ([nPulse, nDur, nInt].some((n) => Number.isNaN(n) || n < 0)) {
        return 'Pulse / Duração / Intervalo precisam ser números válidos (>= 0).';
      }
    }

    return null;
  };

  const validateCreate = (): string | null => {
    if (!deviceId.trim()) return 'DeviceId é obrigatório.';

    // stationCode vira station/stationId também, então mantemos obrigatório
    if (!stationCode.trim()) return 'Posto é obrigatório.';

    if (deviceType === 'VACUUM') {
      if (!name.trim()) return 'Nome é obrigatório para Aspirador.';
    }

    if (deviceType === 'LAUNDRY') {
      if (!name.trim()) return 'Nome é obrigatório para Lavanderia.';
      const types = parseLaundryTypes(laundryTypesText);
      if (types.length === 0) return 'Types é obrigatório (ex: L12,S12).';

      // No cadastro, pulse/duration/interval podem ser 0, mas se preencher tem que ser número válido
      const vals = [
        { label: 'Pulse', value: pulse },
        { label: 'PulseDuration', value: pulseDuration },
        { label: 'PulseInterval', value: pulseInterval },
      ];

      for (const v of vals) {
        if (v.value.trim() !== '' && (Number.isNaN(Number(v.value)) || Number(v.value) < 0)) {
          return `${v.label} precisa ser um número válido (>= 0).`;
        }
      }
    }

    if (deviceType === 'WASH') {
      // payload: washingMachine + stationCode -> ok
    }

    return null;
  };

  const handleTest = async () => {
    setErrorMsg('');
    setSuccessMsg('');

    const validationError = validateTest();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    const payload = buildTestPayload();

    try {
      setLoading(true);
      const response = await axios.post(url, payload);
      console.log('Requisição bem-sucedida', { url, payload, response: response.data });
      setSuccessMsg('Teste enviado com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar comando', error);
      setErrorMsg('Erro ao enviar comando. Veja o console para detalhes.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setErrorMsg('');
    setSuccessMsg('');

    const validationError = validateCreate();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    const payload = buildCreatePayload();

    try {
      setLoading(true);

      if (deviceType === 'VACUUM') await createVacuumCleaner(payload);
      else if (deviceType === 'WASH') await createCarWashing(payload);
      else await createLaundryMachine(payload);

      console.log('Cadastro OK', { deviceType, payload });

      setSuccessMsg('Cadastro realizado com sucesso!');
    } catch (error) {
      console.error('Erro ao cadastrar dispositivo', error);
      setErrorMsg('Erro ao cadastrar dispositivo. Veja o console para detalhes.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={1} sx={{ p: 4, borderRadius: 2, maxWidth: 800 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
          Testar / Cadastrar Dispositivo
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: "medium" }}>
              Modo
            </Typography>
            <ToggleButtonGroup
              color="primary"
              value={mode}
              exclusive
              onChange={(_, val) => val && setMode(val)}
              size="small"
            >
              <ToggleButton value="TEST">TESTE</ToggleButton>
              <ToggleButton value="CREATE">CADASTRO</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: "medium" }}>
              Ambiente
            </Typography>
            <ToggleButtonGroup
              color="primary"
              value={env}
              exclusive
              onChange={(_, val) => val && setEnv(val)}
              size="small"
            >
              <ToggleButton value="PRD">PRD</ToggleButton>
              <ToggleButton value="DEV">DEV</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            select
            fullWidth
            label="Tipo"
            variant="outlined"
            value={deviceType}
            onChange={(e) => setDeviceType(e.target.value as DeviceType)}
          >
            <MenuItem value="VACUUM">Aspirador</MenuItem>
            <MenuItem value="WASH">Lavação</MenuItem>
            <MenuItem value="LAUNDRY">Lavanderia</MenuItem>
          </TextField>
          <Box sx={{ width: '100%', display: { xs: 'none', sm: 'block' } }} />
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Posto"
            variant="outlined"
            value={stationCode}
            onChange={(e) => setStationCode(e.target.value)}
            placeholder="Ex: Z7"
          />
          {mode === 'TEST' && deviceType === 'VACUUM' ? (
            <TextField
              fullWidth
              label="Máscara"
              variant="outlined"
              value={mask}
              onChange={(e) => setMask(e.target.value)}
              placeholder="Ex: 4"
            />
          ) : (
            <TextField
              fullWidth
              label="DeviceId"
              variant="outlined"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              placeholder="Ex: z7asp1"
            />
          )}
        </Stack>

        {mode === 'CREATE' && (deviceType === 'VACUUM' || deviceType === 'LAUNDRY') && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Nome"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Aspirador 01"
              />
              <Box sx={{ width: '100%', display: { xs: 'none', sm: 'block' } }} />
            </Stack>
          </>
        )}

        <Divider sx={{ mb: 3 }} />

        {mode === 'TEST' && deviceType === 'VACUUM' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Tempo ligado (ms)"
              variant="outlined"
              value={timeOn}
              onChange={(e) => setTimeOn(e.target.value)}
              placeholder="Ex: 1000"
              type="number"
            />
            <Box sx={{ width: '100%', display: { xs: 'none', sm: 'block' } }} />
          </Stack>
        )}

        {mode === 'TEST' && deviceType === 'WASH' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <TextField
              select
              fullWidth
              label="Tipo de lavação"
              variant="outlined"
              value={washingType}
              onChange={(e) => setWashingType(e.target.value as any)}
            >
              <MenuItem value="L1">L1</MenuItem>
              <MenuItem value="L2">L2</MenuItem>
              <MenuItem value="L3">L3</MenuItem>
              <MenuItem value="L4">L4</MenuItem>
            </TextField>
            <Box sx={{ width: '100%', display: { xs: 'none', sm: 'block' } }} />
          </Stack>
        )}

        {mode === 'TEST' && deviceType === 'LAUNDRY' && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                select
                fullWidth
                label="Tipo de lavanderia (teste)"
                variant="outlined"
                value={laundryType}
                onChange={(e) => setLaundryType(e.target.value as 'L' | 'S')}
              >
                <MenuItem value="L">Lavar (L)</MenuItem>
                <MenuItem value="S">Secar (S)</MenuItem>
              </TextField>
              <Box sx={{ width: '100%', display: { xs: 'none', sm: 'block' } }} />
            </Stack>

            <Divider sx={{ mb: 3 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Quantidade de pulsos"
                variant="outlined"
                value={pulse}
                onChange={(e) => setPulse(e.target.value)}
                placeholder="Ex: 5"
                type="number"
              />
              <TextField
                fullWidth
                label="Duração do pulso"
                variant="outlined"
                value={pulseDuration}
                onChange={(e) => setPulseDuration(e.target.value)}
                placeholder="Ex: 300"
                type="number"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Intervalo entre os pulsos"
                variant="outlined"
                value={pulseInterval}
                onChange={(e) => setPulseInterval(e.target.value)}
                placeholder="Ex: 200"
                type="number"
              />
              <Box sx={{ width: '100%', display: { xs: 'none', sm: 'block' } }} />
            </Stack>
          </>
        )}

        {mode === 'CREATE' && deviceType === 'WASH' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Cadastro de <b>Lavação</b> usa <Box component="code" sx={{ bgcolor: "grey.100", px: 0.5 }}>washingMachine</Box> = DeviceId e <Box component="code" sx={{ bgcolor: "grey.100", px: 0.5 }}>stationCode</Box> = Posto.
            </Typography>
          </Box>
        )}

        {mode === 'CREATE' && deviceType === 'VACUUM' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Cadastro de <b>Aspirador</b> usa <Box component="code" sx={{ bgcolor: "grey.100", px: 0.5 }}>station</Box> = Posto.
            </Typography>
          </Box>
        )}

        {mode === 'CREATE' && deviceType === 'LAUNDRY' && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Types (ex: L12,S12)"
                variant="outlined"
                value={laundryTypesText}
                onChange={(e) => setLaundryTypesText(e.target.value)}
                placeholder="Ex: L12,S12"
              />
              <Box sx={{ width: '100%', display: { xs: 'none', sm: 'block' } }} />
            </Stack>

            <Divider sx={{ mb: 3 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Quantidade de pulsos (opcional)"
                variant="outlined"
                value={pulse}
                onChange={(e) => setPulse(e.target.value)}
                placeholder="Ex: 0"
                type="number"
              />
              <TextField
                fullWidth
                label="Duração do pulso (opcional)"
                variant="outlined"
                value={pulseDuration}
                onChange={(e) => setPulseDuration(e.target.value)}
                placeholder="Ex: 0"
                type="number"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Intervalo entre pulsos (opcional)"
                variant="outlined"
                value={pulseInterval}
                onChange={(e) => setPulseInterval(e.target.value)}
                placeholder="Ex: 0"
                type="number"
              />
              <Box sx={{ width: '100%', display: { xs: 'none', sm: 'block' } }} />
            </Stack>
          </>
        )}

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
          {mode === 'TEST' ? (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              <b>URL:</b> {url}
              <br />
              <b>Payload (teste):</b> {JSON.stringify(buildTestPayload())}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              <b>Payload (cadastro):</b> {JSON.stringify(buildCreatePayload())}
            </Typography>
          )}
        </Box>

        {!!errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMsg}
          </Alert>
        )}
        {!!successMsg && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMsg}
          </Alert>
        )}

        <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
          <Button
            title={loading ? 'Enviando...' : 'Testar'}
            onClick={handleTest}
            disabled={loading || mode !== 'TEST'}
          />
          <Button
            title={loading ? 'Enviando...' : 'Cadastrar'}
            onClick={handleCreate}
            disabled={loading || mode !== 'CREATE'}
          />
        </Stack>
      </Paper>
    </Box>
  );
};

export default TestRegisterDevice;
